import React, { FC, useEffect } from 'react'

import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'

import { fetchUserPhotosAction } from 'actions/photo/photoViewerAction'
import { useLayoutContext } from 'common/hooks/useLayoutContext'
import { useShallowEqualSelector } from 'common/hooks/useShallowEqualSelector'
import { Colors } from 'common-constants/colors'
import { PhotoViewerParams } from 'components/page/PhotoViewer/PhotoViewer.paths'
import { PhotoViewerMain } from 'components/page/PhotoViewer/PhotoViewerMain'
import { PhotoViewerMainWithoutNotice } from 'components/page/PhotoViewer/PhotoViewerMainWithoutNotice'
import { ratingPath } from 'components/page/RatingWave/Rating.paths'
import {
  AnimatedWatch,
  Loading,
} from 'components/presentational/AnimatedWatch/AnimatedWatch'
import { Replace } from 'components/system/Replace'
import { push } from 'functions/router'
import { useAuthorizationPath } from 'hooks/useAuthorizationLink'
import { useReducersInsert } from 'hooks/useReducersInsert'
import { photoViewerReducer } from 'reducers/photo/photoViewerReducer'
import { profileReducer } from 'reducers/profile/profileReducer'
import { settingsReducer } from 'reducers/settings/settingsReducer'

export const PhotoViewerDefault: FC = () => {
  useReducersInsert({
    settingsReducer: settingsReducer,
    profile: profileReducer,
    photoViewerReducer: photoViewerReducer,
  })

  const dispatch = useDispatch()
  const { baseUrl } = useLayoutContext()
  const { userId, photoId } = useParams<PhotoViewerParams>()

  const {
    isLoading,
    isIncognitoStatus,
    currentUserId,
    photoUserId,
    photos,
    authorized,
    incognitoStatusUpdating,
    isRestoreDeletedPhoto,
    isDeleted,
  } = useShallowEqualSelector(
    ({
      userReducer: { userId: currentUserId },
      photoViewerReducer: {
        userId,
        photos,
        isLoading,
        isIncognitoStatus,
        isRestoreDeletedPhoto,
        isDeleted,
      },
      authorizationReducer: { authorized },
      settingsReducer: { incognitoStatusUpdating },
    }) => ({
      photoUserId: userId,
      photos,
      currentUserId,
      isLoading,
      isDeleted,
      isIncognitoStatus,
      authorized,
      incognitoStatusUpdating,
      isRestoreDeletedPhoto,
    })
  )

  /**
   * Странная логика загрузки, хотя вроде только при монтировании должна быть загрузка.
   * https://redmine.mamba.ru/issues/116632
   */
  useEffect(() => {
    if (!photoId) {
      console.error('photoId for photoViewer not found - PhotoViewerDefault.js')
      dispatch(push(baseUrl))
      return
    }

    // Обходит баг на сервере https://redmine.mamba.ru/issues/119428#note-7
    // Ждем, пока обновится статус инкогнито, чтобы сервер не прислал нам заблюренные фото
    if (incognitoStatusUpdating) {
      return
    }

    if (photoUserId !== Number(userId) && !isDeleted) {
      dispatch(fetchUserPhotosAction(Number(userId)))
    }
  }, [
    userId,
    photoId,
    dispatch,
    baseUrl,
    photoUserId,
    incognitoStatusUpdating,
    isDeleted,
  ])

  useEffect(() => {
    if (isRestoreDeletedPhoto) {
      dispatch(fetchUserPhotosAction(Number(userId)))
    }
  }, [dispatch, isRestoreDeletedPhoto, userId])

  const authorizationPath = useAuthorizationPath()

  /**
   * Редирект на главную если нет фото https://youtrack.mamba.ru/issue/M-9703
   * */
  const redirectToRating =
    photos.length > 0 &&
    photos.every((photo) => photo.photoId !== Number(photoId))

  if (isLoading || isDeleted) {
    return (
      <Loading>
        <AnimatedWatch colorMambaPreloader={Colors.white} />
      </Loading>
    )
  }

  if (isIncognitoStatus && !authorized) {
    return <Replace to={authorizationPath} uid="PhotoViewerDefault-1" />
  }

  if (photos.length === 0) {
    return <Replace to={baseUrl} uid="PhotoViewerDefault-2" />
  }

  if (redirectToRating) {
    return <Replace to={ratingPath} uid="PhotoViewerDefault-3" />
  }

  // для текущего авторизованного пользователя просмотр фото без notice.
  if (currentUserId === Number(userId)) {
    return <PhotoViewerMainWithoutNotice isAttachPhotos={false} />
  }

  return <PhotoViewerMain key={userId} />
}
